import { useMemo, useState } from "react";
import { useData } from "@/context/DataContext";
import { presetRange } from "@/lib/dateRanges";
import { useApiGet } from "@/lib/useApiGet";
import { DashFilters } from "@/components/dashboard/DashFilters";
import { LeakStrip } from "@/components/dashboard/LeakStrip";
import { MoneySection } from "@/components/dashboard/MoneySection";
import { CloserTable, SetterTable } from "@/components/dashboard/RepTables";
import { LossChart } from "@/components/dashboard/LossChart";
import { ProjectionSection } from "@/components/dashboard/ProjectionSection";
import { ExportButton } from "@/components/crm/ExportButton";

export default function DashboardPage() {
  const { reps, leads } = useData();
  const [preset, setPreset] = useState("this_month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [repId, setRepId] = useState("");
  const [source, setSource] = useState("");
  const [version, setVersion] = useState(0);
  const range = presetRange(preset, custom);
  const sources = useMemo(() => [...new Set(leads.map((l) => l.source).filter(Boolean))].sort(), [leads]);
  const params = { rep_id: repId, source, date_from: range.from, date_to: range.to };
  const { data, error, loading } = useApiGet("/dashboard", params, [repId, source, range.from, range.to, leads, version]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Dashboard</h1>
          <p className="text-sm text-slate-400">Calculated live from lead cards and daily activity.</p>
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          {data && <p data-testid="dash-lead-count" className={`font-mono text-xs text-slate-400 ${loading ? "opacity-50" : ""}`}>{data.lead_count} leads · {data.activity_count} activity days</p>}
          <ExportButton testid="dash-export-btn" path="/export/dashboard.xlsx" params={params} prefix="sales-dashboard" />
        </div>
      </div>
      <DashFilters {...{ preset, setPreset, custom, setCustom, range, repId, setRepId, source, setSource, reps, sources }} />
      {error && <p data-testid="dash-error" className="text-sm text-amber-400">{error}</p>}
      {!data ? <p className="text-sm text-slate-400" data-testid="dash-loading">Loading dashboard…</p> : (
        <div className={`space-y-8 transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="dash-content">
          <LeakStrip leaks={data.leaks} />
          <MoneySection data={data} onGoalSaved={() => setVersion((v) => v + 1)} />
          <SetterTable data={data} />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="min-w-0 xl:col-span-2"><CloserTable data={data} /></div>
            <LossChart data={data.loss_reasons} agingCount={data.follow_up_aging_count} />
          </div>
          <ProjectionSection deps={[leads, version]} />
        </div>
      )}
    </div>
  );
}
